/**
 * The token layer: every primitive value the interface is made of, named once.
 *
 * This is the bottom of the design system (docs/28). Nothing above it may write
 * a colour, a length or a duration that is not one of these, and nothing below
 * it exists — a token has no dependencies, which is what makes the layer
 * portable on its own.
 *
 * **Organised by role, not by where it is used.** `--ui-panel-2` is not "the
 * colour of a panel header"; it is *the second surface step*, and a panel
 * header is one of the things that needs one. Every token carries a sentence
 * saying when to reach for it — those live in `tokenDocs.ts`, next door, and
 * the split is deliberate: a token nobody knows when to use gets bypassed, and
 * a bypassed token is a hard-coded literal with extra steps, but seven
 * kilobytes of prose has no business inside a game's JavaScript bundle. The
 * documentation is imported by the gallery and by the tests, and by nothing the
 * player downloads. `tests/designSystem.test.ts` requires the two key sets to
 * match exactly in both directions, so the value and its explanation cannot
 * drift apart.
 *
 * **One spelling.** These are declared here and nowhere else.
 * `tests/designSystem.test.ts` fails if a `--ui-*` property is declared in a
 * second place, if a `var(--ui-*)` names a token that does not exist, or if a
 * token is declared and never read. The one deliberate exception is
 * `CONTRAST_VARS` in `src/ui/settingsModel.ts`, which *overrides* a subset of
 * these on the same element; the test checks every key in it against this
 * table, so a rename cannot leave it pointing at a property nobody declares —
 * which is exactly what `--ui-fg-inv` was doing before docs/28.
 *
 * **The type scale is written twice, and only one of them scales.** This is the
 * largest live inconsistency the extraction found and did not fix, so it is
 * stated at the top of the file rather than buried: chrome that shares the
 * screen with the running game writes its sizes as `calc(10px * var(--ui-u))`
 * and follows the interface-scale setting; panels write `font-size: 10px` and
 * do not. Both are the same number. `--ui-fs-label` and `--ui-fs-tiny` now name
 * the second one, which turns "make the panels scale too" from a fifty-site
 * edit into a one-line edit of this table — and that edit is a **visible
 * change** at three of the four scale settings, which is exactly why it is not
 * made here. docs/28 §5 item 12 and §6.4 have the numbers and the one-liner.
 *
 * **What is not here.** Five channel colours — `--ui-hp`, `--ui-shield`,
 * `--ui-rhythm`, `--ui-dash`, `--ui-flight` (issue #171) — are the *game's*,
 * and live in `src/ui/style.ts` with the rest of the game layer. They are the
 * cheapest demonstration of where the portability seam sits: the design
 * system does not know this application has hit points, and nothing in
 * `src/ds/` reads them.
 */

/**
 * The scale that is *not* a scale: what `--ui-fs` reads at the default option,
 * and the denominator `--ui-u` divides by.
 *
 * It lives here, at the bottom of the system, and `src/ui/settingsModel.ts`
 * re-exports it — the direction matters. The design system may not import from
 * the application, or it is not a design system; the application may import
 * from the design system, which is the entire arrangement. It used to be
 * declared in `settingsModel.ts` and imported *up* into the sheet, which was
 * the right instinct (one spelling) pointed the wrong way.
 *
 * A second literal `12` written into the stylesheet is the drift this pair
 * exists to prevent: `--ui-u` would stop being 1 at Normal, and every box in
 * the interface would quietly change size for players who never touched the
 * control.
 */
export const UI_FS_BASE_PX = 12;

/** `[custom property, value]`. Values are written into the sheet verbatim. */
export type Token = readonly [name: string, value: string];

/**
 * Every token, in the order it is written into the sheet.
 *
 * Order inside one rule does not affect the cascade, so this is grouped for
 * reading. It is a flat array rather than a nested object because the examples
 * page, the docs table and the "declared exactly once" test all want to walk
 * it, and a shape that needs flattening at three call sites is a shape that
 * will be flattened three different ways.
 *
 * Two rules for spending `--ui-u`, since it is the one token whose misuse is
 * silent:
 *   - text, gaps and padding take `calc(Npx * var(--ui-u))` and may land on a
 *     fraction of a pixel; type renders fine there;
 *   - small hard-edged chrome — status boxes, meter segments, pips, bar
 *     heights, key cells — takes `round(nearest, calc(Npx * var(--ui-u)), 1px)`.
 *     A 12x7 segment at 0.9167 is 11x6.42, and a 0.42px sliver of border is the
 *     blur a pixel-art discipline exists to avoid.
 */
export const TOKENS: readonly Token[] = [

  // surface — what a box is made of
  ['--ui-bg', '#07070b'],
  ['--ui-scrim', 'rgba(5,5,9,.80)'],
  ['--ui-panel', '#0b0c11'],
  ['--ui-panel-2', '#10121a'],
  ['--ui-panel-3', '#161923'],
  ['--ui-well', '#05050a'],
  ['--ui-cell', '#0a0b10'],

  // line — three edges; in a system with no shadows the edge is the elevation
  ['--ui-line', '#262b36'],
  ['--ui-line-hot', '#3c4454'],
  ['--ui-line-soft', '#14161d'],

  // ink — three readable weights and one inverse
  ['--ui-fg', '#d7d2c8'],
  ['--ui-fg-dim', '#8d8779'],
  ['--ui-fg-mute', '#5b5750'],
  ['--ui-ink-inv', '#000'],

  // accent — one warm for chosen, one cool for derived
  ['--ui-accent', '#e0a860'],
  ['--ui-accent-2', '#6fd3c7'],

  // status — the second half of a signal, never the whole of it
  ['--ui-good', '#7fd17f'],
  ['--ui-bad', '#d2544a'],
  ['--ui-warn', '#e0b040'],

  // rarity — a six-step categorical ramp
  ['--ui-r-common', '#8d8779'],
  ['--ui-r-uncommon', '#7fb069'],
  ['--ui-r-rare', '#5b9bd5'],
  ['--ui-r-epic', '#a97fd0'],
  ['--ui-r-relic', '#e0a860'],
  ['--ui-r-cursed', '#c0506e'],

  // type — one family, three sizes
  ['--ui-font', 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'],
  ['--ui-fs', `${UI_FS_BASE_PX}px`],
  ['--ui-fs-label', '10px'],
  ['--ui-fs-tiny', '9px'],

  // scale — the interface scale, as a number you can multiply by
  ['--ui-u', `calc(var(--ui-fs) / ${UI_FS_BASE_PX}px)`],

  // space — one named length
  ['--ui-gap', '6px'],

  // motion — one duration, one easing, one transition
  ['--ui-dur', '140ms'],
  ['--ui-ease', 'linear'],

  // elevation — the only box-shadow in the system
  ['--ui-mark', 'inset 2px 0 0 var(--ui-accent)'],
];

/** `--name -> value`, for the tests, the docs and the gallery. */
export const TOKEN_VALUE: ReadonlyMap<string, string> = new Map(TOKENS);

/** The token declarations, as they are written into `.ui-root`. */
export const TOKENS_CSS = /* css */ `
.ui-root {
${TOKENS.map(([n, v]) => `  ${n}: ${v};`).join('\n')}
}
`;
